// V2 Tempo — Skill Ladder. Client-side ELO with five visible tiers
// (Bronze → Silver → Gold → Diamond → Master) plus a Grandmaster tail
// for the obsessed. The single biggest D7 lever in a casual competitive
// game: players grind XP for hours, but they REPLAY for hours to keep a
// rank chip from sliding.
//
// Why ELO and not raw W/L:
//   - Beating a Recruit bot is worth less than beating a Commander.
//   - A losing streak shouldn't tank a player who's been winning for
//     weeks; ELO naturally damps that.
//   - The same number ports cleanly to a future server-side leaderboard
//     (the docs/13_PORTAL_CONSTRAINTS.md §8 backend-V3 work).
//
// Why client-side for now:
//   - V2 has no profile backend. ELO lives in localStorage + cloud-save
//     under the same `signalclash.profile.*` namespace as XP / streak /
//     unlocks. When the V3 backend lands, the migration is one POST.
//   - Local ELO can't be "cheated" in any way that affects another
//     player, because every other player has their own local ELO. The
//     only thing inflating your own ELO costs you is the meaningfulness
//     of the chip, which self-regulates.
//
// Sourcing opponent ELO:
//   - Bot matches: the bot's difficulty maps to an implied ELO (Recruit
//     900, Tactical 1100, Commander 1350). Calibrated so a typical new
//     player at 1000 ELO beating Recruit gains ~22, losing to Recruit
//     drops ~10; beating Commander gains ~28, losing drops ~4.
//   - Network matches: until matchmaking knows ELO, every human is
//     treated as a 1200-ELO opponent — a slight stretch goal for a
//     starting player, slightly underwhelming for a veteran. Good
//     enough as a single-number placeholder.

const ELO_KEY = 'signalclash.profile.elo';
const PEAK_ELO_KEY = 'signalclash.profile.eloPeak';
export const STARTING_ELO = 1000;
const K_FACTOR = 24;

const BOT_DIFFICULTY_ELO: Record<string, number> = {
  recruit: 900,
  tactical: 1100,
  commander: 1350,
};
const NETWORK_OPPONENT_ELO = 1200;

export interface RankTier {
  id: 'bronze' | 'silver' | 'gold' | 'diamond' | 'master' | 'grandmaster';
  name: string;
  min: number;
  max: number;
  // Hex color used on the rank chip in the main-menu profile card,
  // EOM rank chip, and the ProfilePanel record cell.
  color: string;
  // Accent shadow color for the glow.
  glow: string;
}

export const RANK_TIERS: readonly RankTier[] = [
  { id: 'bronze',      name: 'Bronze',      min: 0,    max: 1099, color: '#cd7f32', glow: 'rgba(205,127,50,0.45)' },
  { id: 'silver',      name: 'Silver',      min: 1100, max: 1249, color: '#c0c8d4', glow: 'rgba(192,200,212,0.45)' },
  { id: 'gold',        name: 'Gold',        min: 1250, max: 1399, color: '#ffe680', glow: 'rgba(255,230,128,0.50)' },
  { id: 'diamond',     name: 'Diamond',     min: 1400, max: 1549, color: '#6fdcff', glow: 'rgba(110,220,255,0.55)' },
  { id: 'master',      name: 'Master',      min: 1550, max: 1749, color: '#c66bff', glow: 'rgba(198,107,255,0.55)' },
  { id: 'grandmaster', name: 'Grandmaster', min: 1750, max: 99999, color: '#ff7a5a', glow: 'rgba(255,122,90,0.60)' },
] as const;

export function loadElo(): number {
  if (typeof localStorage === 'undefined') return STARTING_ELO;
  try {
    const raw = localStorage.getItem(ELO_KEY);
    if (raw === null) return STARTING_ELO;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : STARTING_ELO;
  } catch {
    return STARTING_ELO;
  }
}

export function loadPeakElo(): number {
  if (typeof localStorage === 'undefined') return STARTING_ELO;
  try {
    const raw = localStorage.getItem(PEAK_ELO_KEY);
    if (raw === null) return STARTING_ELO;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : STARTING_ELO;
  } catch {
    return STARTING_ELO;
  }
}

function writeElo(value: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ELO_KEY, String(Math.max(0, Math.round(value))));
  } catch {
    // Quota / privacy mode — non-fatal.
  }
}

function writePeakElo(value: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PEAK_ELO_KEY, String(Math.max(0, Math.round(value))));
  } catch {
    // Quota / privacy mode — non-fatal.
  }
}

export function tierFor(elo: number): RankTier {
  for (const tier of RANK_TIERS) {
    if (elo >= tier.min && elo <= tier.max) return tier;
  }
  // Below 0 or above the cap — clamp.
  return elo < 0 ? RANK_TIERS[0]! : RANK_TIERS[RANK_TIERS.length - 1]!;
}

// Progress within current tier: 0..1, plus the raw ELO points needed
// to reach the next tier. For the top tier we report 1.0 with 0 to go.
export function tierProgress(elo: number): { ratio: number; toNext: number; nextTier: RankTier | null } {
  const tier = tierFor(elo);
  const tierIndex = RANK_TIERS.indexOf(tier);
  const next = tierIndex < RANK_TIERS.length - 1 ? RANK_TIERS[tierIndex + 1]! : null;
  if (next === null) return { ratio: 1, toNext: 0, nextTier: null };
  const span = next.min - tier.min;
  const into = elo - tier.min;
  const ratio = span > 0 ? Math.max(0, Math.min(1, into / span)) : 1;
  return { ratio, toNext: Math.max(0, next.min - elo), nextTier: next };
}

export interface RankUpdate {
  beforeElo: number;
  afterElo: number;
  delta: number;
  beforeTier: RankTier;
  afterTier: RankTier;
  promoted: boolean;
  demoted: boolean;
  newPeak: boolean;
}

// Resolve opponent ELO for the match. Bot matches read difficulty;
// network matches use a single placeholder until ELO is plumbed through
// matchmaking.
export function opponentEloFor(opts: { mode: 'local' | 'network'; difficulty?: string }): number {
  if (opts.mode === 'network') return NETWORK_OPPONENT_ELO;
  const d = (opts.difficulty ?? 'tactical').toLowerCase();
  return BOT_DIFFICULTY_ELO[d] ?? NETWORK_OPPONENT_ELO;
}

// The actual ELO update. Reads current ELO, computes the new value,
// writes to localStorage, returns the full transition object so the EOM
// can show a chip animation and the analytics can record the delta.
export function applyMatchResult(opts: {
  won: boolean;
  mode: 'local' | 'network';
  difficulty?: string;
}): RankUpdate {
  const beforeElo = loadElo();
  const peakBefore = loadPeakElo();
  const opponentElo = opponentEloFor(opts);
  const expected = 1 / (1 + Math.pow(10, (opponentElo - beforeElo) / 400));
  const actual = opts.won ? 1 : 0;
  const rawDelta = K_FACTOR * (actual - expected);
  // Soft floor on loss: never lose more than 16 ELO in a single match,
  // never gain more than 32. Keeps the system from feeling brutal after
  // a single bad match and from gaming a Recruit bot for huge gains.
  const delta = Math.round(Math.max(-16, Math.min(32, rawDelta)));
  const afterElo = Math.max(0, beforeElo + delta);
  writeElo(afterElo);
  const newPeak = afterElo > peakBefore;
  if (newPeak) writePeakElo(afterElo);
  const beforeTier = tierFor(beforeElo);
  const afterTier = tierFor(afterElo);
  return {
    beforeElo,
    afterElo,
    delta,
    beforeTier,
    afterTier,
    promoted: RANK_TIERS.indexOf(afterTier) > RANK_TIERS.indexOf(beforeTier),
    demoted: RANK_TIERS.indexOf(afterTier) < RANK_TIERS.indexOf(beforeTier),
    newPeak,
  };
}
